//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { BUILDER_TYPES, DATA_SOURCE_NAME_PATTERN, FIELD_KEYS, IMAGE_LINK_DESTINATIONS, PRODUCT_OPTIONS } from '@email-builder/constants/emailBuilderConstants';
import { ILayoutSettings, IOptionalFilters, ISocialInformation } from '@email-builder/models/emailBuilderStateModel';
import gaApi from 'app/initialization/ga/ga-api';

export function generateDataSourceName(clientName: string) {
  return `${clientName} ${DATA_SOURCE_NAME_PATTERN}`;
}

export function formatUsername(username: string) {
  return username ? `@${username}` : '';
}

// Convert hex to Rgba
export function hexToRgba(hex: string, opacity: number) {
  // Expand shorthand form (e.g. '03F') to full form (e.g. '0033FF')
  const shorthandRegex = /^#([a-f\d])([a-f\d])([a-f\d])$/i;
  const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => {
    return '#' + r + r + g + g + b + b;
  });

  const result = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
  return result ?
    `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${opacity / 100}`
    : null;
}

export function chooseFanreelOrGallery(hasGalleriesEnabled: boolean) {
  return {
    plural: hasGalleriesEnabled ? 'Galleries' : 'Fanreels',
    singular: hasGalleriesEnabled ? 'Gallery' : 'Fanreel',
  };
}

export function getError(fieldKey: FIELD_KEYS) {
  let error = '';

  switch (fieldKey) {
    case FIELD_KEYS.LABEL:
    case FIELD_KEYS.PRODUCT_ID:
    case FIELD_KEYS.PRODUCT_URL:
    case FIELD_KEYS.CATEGORY:
    case FIELD_KEYS.USERNAME:
    case FIELD_KEYS.TAG:
    case FIELD_KEYS.ADDITIONAL_FILTERS:
      error = 'Please enter a single filter with no spaces.';
      break;

    // TODO: Delete once we've moved to the responsive inputs
    case FIELD_KEYS.NUM_ROWS:
    case FIELD_KEYS.NUM_COLUMNS:
    case FIELD_KEYS.IMAGE_WIDTH:
    case FIELD_KEYS.IMAGE_HEIGHT:

    case FIELD_KEYS.NUM_COLUMNS_DESKTOP:
    case FIELD_KEYS.NUM_ROWS_DESKTOP:
    case FIELD_KEYS.NUM_COLUMNS_MOBILE:
    case FIELD_KEYS.NUM_ROWS_MOBILE:
      error = 'Please enter a valid number.';
      break;
    case FIELD_KEYS.URL:
      error = `Please enter a valid URL. It must start with 'http://' or 'https://`;
      break;
    case FIELD_KEYS.BACKGROUND_COLOR:
    case FIELD_KEYS.USERNAME_TEXT_COLOR:
      error = 'Please enter a valid color hex code, including the #. Example: #F968B4';
      break;
  }

  return error;
}

// Form validators

export function validateHex(hexValue: string) {
  const pattern = new RegExp('^#[0-9a-f]{6}$', 'i');
  return pattern.test(hexValue);
}

export function validatePosNumUnderOneThousand(num: string) {
  const pattern = new RegExp('^([1-9][0-9]{0,2}|1000)$', 'i');
  return pattern.test(num);
}

export function validateNoSpaces(str: string) {
  const pattern = new RegExp('^\\S*$', 'i');
  return pattern.test(str);
}

// Google analytics

export function recordCampaignGAEvents(
  optionalFilters: IOptionalFilters,
  layoutSettings: ILayoutSettings,
  socialInformation: ISocialInformation,
  imageLink: IMAGE_LINK_DESTINATIONS,
  productOption: PRODUCT_OPTIONS,
  uuid: string,
) {
  gaApi.recordEvent(
    gaApi.CATEGORY_EMAIL_BUILDER,
    gaApi.ACTION_EMAIL_BUILDER_COPY_HTML_CAMPAIGN,
    uuid,
  );

  recordOptionalFilters(optionalFilters, BUILDER_TYPES.CAMPAIGN);
  recordLayout(layoutSettings, BUILDER_TYPES.CAMPAIGN);

  if (socialInformation.displayUsername) {
    for (const socialKey of Object.keys(socialInformation)) {
      gaApi.recordEvent(
        gaApi.CATEGORY_EMAIL_BUILDER,
        gaApi.ACTION_EMAIL_BUILDER_SET_SOCIAL,
        `key=${socialKey},value=${socialInformation[socialKey]}`,
      );
    }
  } else {
    gaApi.recordEvent(
      gaApi.CATEGORY_EMAIL_BUILDER,
      gaApi.ACTION_EMAIL_BUILDER_SET_SOCIAL,
      `key=displayUsername,value=false`,
    );
  }

  gaApi.recordEvent(
    gaApi.CATEGORY_EMAIL_BUILDER,
    gaApi.ACTION_EMAIL_BUILDER_SET_OPTION,
    `key=imageLink,value=${imageLink}`,
  );

  if (imageLink === IMAGE_LINK_DESTINATIONS.PRODUCT) {
    gaApi.recordEvent(
      gaApi.CATEGORY_EMAIL_BUILDER,
      gaApi.ACTION_EMAIL_BUILDER_SET_OPTION,
      `key=productOption,value=${productOption}`,
    );
  }
}

export function recordEvergreenGAEvents(
  optionalFilters: IOptionalFilters,
  layoutSettings: ILayoutSettings,
  uuid: string
) {
  gaApi.recordEvent(
    gaApi.CATEGORY_EMAIL_BUILDER,
    gaApi.ACTION_EMAIL_BUILDER_COPY_HTML_EVERGREEN,
    uuid,
  );

  recordOptionalFilters(optionalFilters, BUILDER_TYPES.EVERGREEN);
  recordLayout(layoutSettings, BUILDER_TYPES.EVERGREEN);
}

function recordOptionalFilters(optionalFilters: IOptionalFilters, builderType: BUILDER_TYPES) {
  const action = builderType === BUILDER_TYPES.EVERGREEN ?
    gaApi.ACTION_EMAIL_BUILDER_SET_FILTER_EVERGREEN :
    gaApi.ACTION_EMAIL_BUILDER_SET_FILTER_CAMPAIGN;

  for (const filterKey of Object.keys(optionalFilters)) {
    gaApi.recordEvent(
      gaApi.CATEGORY_EMAIL_BUILDER,
      action,
      `key=${filterKey},value=${optionalFilters[filterKey]}`,
    );
  }
}

function recordLayout(layoutSettings: ILayoutSettings, builderType: BUILDER_TYPES) {
  const action = builderType === BUILDER_TYPES.EVERGREEN ?
  gaApi.ACTION_EMAIL_BUILDER_SET_LAYOUT_EVERGREEN :
  gaApi.ACTION_EMAIL_BUILDER_SET_LAYOUT_CAMPAIGN;

  for (const layoutKey of Object.keys(layoutSettings)) {
    gaApi.recordEvent(
      gaApi.CATEGORY_EMAIL_BUILDER,
      action,
      `key=${layoutKey},value=${layoutSettings[layoutKey]}`,
    );
  }
}

// ReactDom.renderToStaticMarkup() converts certain reserved characters to HTML entities
// Since we want to preserve the original characters, this method is used to convert the entities
// back into their original characters
export const convertHtmlEntitiesToCharacters = (code: string): string => {
  return code.
    replace(/&quot;/gi, '"'). // Replace "
    replace(/&lt;/gi, '<').   // Replace <
    replace(/&gt;/gi, '>').   // Replace >
    replace(/&#x27;/gi, `'`); // Replace '
};

// Removes newlines and multiple spaces, tabs, etc so that embeded HTML looks consistent
export const convertWhitespace = (code: string) => {
  return code.
    replace(/(\r\n|\n|\r)/g, '').  // Remove newlines
    replace(/\s\s+/g, ' ');         // Remove multiple spaces
};

// See Chrome hack comment in EmailBuilderResponsivePreview.tsx
// This method removes the styled component overrides from the embeddable HTML
export const removeStyledComponentClasses = (code: string) => {
  return code.replace(/class="sc-.*?"/g, '');
};

export const conditionallyAddOutlookStyles = (baseStyles: any, showInOutlook: boolean) => {
  return {
    ...(baseStyles),
    ...(!showInOutlook && { msoHide: 'all' }),
  };
};

export const truncateToFourDecimalPlaces = (decimal: number) => {
  return Math.floor(10000 * decimal) / 10000;
};
